import _ from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { Icon } from '../../../ui-toolkit';
import { SingleCheckBox } from '../../../ui-toolkit/indexExportV2';
import { createNodesByKeyMap, createParentByKeyMap, everyChildIsSelected, getChildNodes, getParentNodes, isBranchNode, mapKeysToNodes, selectKey, someChildIsSelected, toTrueByKeyMap, unselectKey, } from './TreeView.helpers';
import { ExpandButton, TreeViewEmpty, TreeViewList } from './TreeView.styling';
export var TreeView = function (_a) {
    var _b = _a.branchSelectionMode, branchSelectionMode = _b === void 0 ? 'node' : _b, emptyState = _a.emptyState, expandedKeys = _a.expandedKeys, filter = _a.filter, filterBy = _a.filterBy, _c = _a.isMulti, isMulti = _c === void 0 ? false : _c, _d = _a.isSelectable, isSelectable = _d === void 0 ? false : _d, nodes = _a.nodes, renderLabel = _a.renderLabel, selectedKeys = _a.selectedKeys, onChange = _a.onChange, onCollapse = _a.onCollapse, onExpand = _a.onExpand, onSelect = _a.onSelect, onUnselect = _a.onUnselect;
    var isExpandedKeysControlled = Boolean(expandedKeys);
    var isSelectedKeysControlled = Boolean(selectedKeys);
    var branchSelectionModeInternal = useMemo(function () {
        if (!isSelectable) {
            return 'none';
        }
        if (isMulti) {
            // When multi select is enabled, the branch selection mode can be anything.
            return branchSelectionMode;
        }
        // When multi select is disabled, the only allowed modes for branch selection are 'node' and 'none'.
        return branchSelectionMode === 'branch' ? 'node' : branchSelectionMode;
    }, [branchSelectionMode, isMulti, isSelectable]);
    var _e = React.useState(toTrueByKeyMap(expandedKeys)), expandedKeysInternal = _e[0], setExpandedKeysInternal = _e[1];
    /**
     * The selected keys state. This state is used to manage the selected keys internally when the component is in
     * uncontrolled mode. When the component is in controlled mode, this state is updated when the `selectedKeys` prop
     * changes. Additionally, the map contains only the selected keys, with the value being `true`. Unselected keys
     * will **not** be present in the map.
     */
    var _f = React.useState(toTrueByKeyMap(selectedKeys)), selectedKeysInternal = _f[0], setSelectedKeysInternal = _f[1];
    /**
     * A map containing all nodes in the tree view, with the key being the node key and the value being the node
     * itself. This map is used to quickly reference a node by its key.
     */
    var nodesByKey = React.useMemo(function () { return createNodesByKeyMap(nodes); }, [nodes]);
    /**
     * A map containing all nodes in the tree view, with the key being the node key and the value being the parent node.
     * This map is used to quickly reference a node's parent by its key.
     */
    var parentByKey = React.useMemo(function () { return createParentByKeyMap(nodes); }, [nodes]);
    /**
     * A map containing all nodes that are visible in the tree view, with the key being the node key and the value
     * being `true`. This map is used to quickly reference whether a node is visible or not. This map is applicable
     * when a filter is applied to the tree view.
     */
    var visibleNodesKeys = useMemo(function () {
        if (!filter) {
            return null;
        }
        var visibleNodes = new Map();
        var getFilterKey = _.isFunction(filterBy) ? filterBy : function (node) { return node.label; };
        var filterNormalized = filter.toLowerCase();
        var checkNode = function (node) {
            return getFilterKey(node).toLowerCase().includes(filterNormalized);
        };
        var traverse = function (node) {
            var isVisible = checkNode(node) ||
                getChildNodes(node).map(checkNode).some(Boolean) ||
                getParentNodes(node, parentByKey).map(checkNode).some(Boolean);
            if (isVisible) {
                visibleNodes.set(node.key, true);
            }
            if (isBranchNode(node)) {
                node.children.forEach(traverse);
            }
        };
        nodes.forEach(traverse);
        return visibleNodes;
    }, [filter, filterBy, nodes]);
    var isNodeVisible = function (node) {
        return !visibleNodesKeys || visibleNodesKeys.has(node.key);
    };
    var visibleNodes = useMemo(function () { return _.filter(nodes, isNodeVisible); }, [nodes, visibleNodesKeys]);
    var expandedKeysPrevious = React.useRef(null);
    useEffect(function () {
        // When the filter changes, reset the expanded keys to only show the nodes that match the filter.
        if (!visibleNodesKeys) {
            if (expandedKeysPrevious.current) {
                // Reset the expanded keys to the state before the filter was applied, if there was one.
                setExpandedKeysInternal(function () { return new Map(expandedKeysPrevious.current); });
                expandedKeysPrevious.current = null;
            }
            return;
        }
        if (!expandedKeysPrevious.current) {
            // Only store the expanded keys before the filter was applied the first time.
            expandedKeysPrevious.current = expandedKeysInternal;
        }
        setExpandedKeysInternal(function () { return new Map(visibleNodesKeys); });
    }, [nodes, visibleNodesKeys]);
    /**
     * When the `expandedKeys` prop changes, update the internal state accordingly. This is necessary to keep the
     * internal state in sync with the prop when in controlled mode.
     */
    React.useEffect(function () {
        if (!expandedKeys)
            return;
        setExpandedKeysInternal(function () {
            return toTrueByKeyMap(expandedKeys);
        });
    }, [expandedKeys]);
    /**
     * When the `selectedKeys` prop changes, update the internal state accordingly. This is necessary to keep the
     * internal state in sync with the prop when in controlled mode.
     */
    React.useEffect(function () {
        if (!selectedKeys)
            return;
        setSelectedKeysInternal(function () {
            return toTrueByKeyMap(selectedKeys);
        });
    }, [selectedKeys]);
    var toggleBranch = function (key) {
        var isExpanded = expandedKeysInternal.get(key);
        if (!isExpandedKeysControlled) {
            setExpandedKeysInternal(function (state) { return new Map(state.set(key, !isExpanded)); });
        }
        var node = nodesByKey.get(key);
        isExpanded ? onCollapse === null || onCollapse === void 0 ? void 0 : onCollapse(node) : onExpand === null || onExpand === void 0 ? void 0 : onExpand(node);
    };
    React.useEffect(function () {
        var selectedKeysInternalArray = Array.from(selectedKeysInternal.keys());
        var hasChanges = !_.isEmpty(_.xor(selectedKeysInternalArray, selectedKeys));
        if (hasChanges) {
            onChange === null || onChange === void 0 ? void 0 : onChange(selectedKeysInternalArray);
        }
    }, [selectedKeysInternal]);
    var selectNode = function (key) {
        if (!isSelectable) {
            return;
        }
        var result = selectKey(key, nodes, nodesByKey, selectedKeysInternal, isMulti, branchSelectionModeInternal);
        if (!isSelectedKeysControlled) {
            setSelectedKeysInternal(function () { return toTrueByKeyMap(result.selectedKeys); });
        }
        onSelect === null || onSelect === void 0 ? void 0 : onSelect(mapKeysToNodes(result.newSelectedKeys, nodesByKey), mapKeysToNodes(result.newUnselectedKeys, nodesByKey));
    };
    var unselectNode = function (key) {
        if (!isSelectable) {
            return;
        }
        var result = unselectKey(key, nodes, nodesByKey, selectedKeysInternal, isMulti, branchSelectionModeInternal);
        if (!isSelectedKeysControlled) {
            setSelectedKeysInternal(function () { return toTrueByKeyMap(result.selectedKeys); });
        }
        onUnselect === null || onUnselect === void 0 ? void 0 : onUnselect(_.map(result.newUnselectedKeys, function (key) { return nodesByKey.get(key); }));
    };
    if (_.isEmpty(visibleNodes)) {
        return !_.isNil(emptyState) ? emptyState : React.createElement(TreeViewEmpty, null, "No available items");
    }
    return (React.createElement(TreeViewList, null, _.map(visibleNodes, function (node) { return (React.createElement(TreeViewNode, { key: node.key, branchSelectionMode: branchSelectionModeInternal, expandedKeys: expandedKeysInternal, isSelectable: isSelectable, node: node, renderLabel: renderLabel, selectedKeys: selectedKeysInternal, shouldRenderNode: isNodeVisible, onSelect: function (node) { return selectNode(node.key); }, onToggleCollapsed: function (node) { return toggleBranch(node.key); }, onUnselect: function (node) { return unselectNode(node.key); } })); })));
};
var TreeViewNode = function (_a) {
    var _b, _c, _d;
    var branchSelectionMode = _a.branchSelectionMode, expandedKeys = _a.expandedKeys, isSelectable = _a.isSelectable, node = _a.node, renderLabel = _a.renderLabel, selectedKeys = _a.selectedKeys, shouldRenderNode = _a.shouldRenderNode, onSelect = _a.onSelect, onToggleCollapsed = _a.onToggleCollapsed, onUnselect = _a.onUnselect;
    var isBranch = isBranchNode(node);
    var isBranchSelectionMode = branchSelectionMode === 'branch' || branchSelectionMode === 'branch-visual';
    var isExpanded = (_b = expandedKeys.get(node.key)) !== null && _b !== void 0 ? _b : false;
    var canBeSelected = isSelectable && (!isBranch || branchSelectionMode !== 'none');
    var isSelected = isBranch && isBranchSelectionMode
        ? everyChildIsSelected(node, selectedKeys)
        : (_c = selectedKeys.get(node.key)) !== null && _c !== void 0 ? _c : false;
    var isIndeterminate = !isSelected && isBranch && someChildIsSelected(node, selectedKeys);
    var label = (_d = renderLabel === null || renderLabel === void 0 ? void 0 : renderLabel(node)) !== null && _d !== void 0 ? _d : node.label;
    var visibleChildren = isBranch ? node.children.filter(shouldRenderNode) : [];
    return (React.createElement(React.Fragment, null,
        React.createElement("li", null,
            !_.isEmpty(visibleChildren) && (React.createElement(ExpandButton, { type: "button", link: true, color: "primary", isExpanded: isExpanded, onClick: function () { return onToggleCollapsed(node); } },
                React.createElement(Icon, { name: "chevronright" }))),
            canBeSelected && (React.createElement(SingleCheckBox, { raw: true, checked: isSelected, isIndeterminate: isIndeterminate, label: node.label, onChange: function () { return (isSelected ? onUnselect(node) : onSelect(node)); } })),
            !canBeSelected && React.createElement("label", null, label)),
        isBranch && isExpanded && (React.createElement(TreeViewList, { isNested: true }, _.map(visibleChildren, function (child) { return (React.createElement(TreeViewNode, { key: child.key, branchSelectionMode: branchSelectionMode, expandedKeys: expandedKeys, shouldRenderNode: shouldRenderNode, isSelectable: isSelectable, node: child, renderLabel: renderLabel, selectedKeys: selectedKeys, onSelect: onSelect, onToggleCollapsed: onToggleCollapsed, onUnselect: onUnselect })); })))));
};
